import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, PolarGrid, Line, RadarChart,
  Legend, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart
} from 'recharts';
import { 
  Users, Briefcase, MapPin, GraduationCap, Building2,
  TrendingUp, Award, Calendar, Building, ChevronDown,
  ChartBar, Filter, Clock, Download, Share2, RefreshCw,
  Binary, BookOpen, Globe2, CheckCircle2, Star, CircleDollarSign,
  UserCheck, Network, LineChart as LineChartIcon, ChevronUp, TrendingDown,  
  FileSpreadsheet,
  FileText,

} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
// Color schemes and constants from your original code
const CHART_COLORS = {
  primary: {
    purple: ['#7C3AED', '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE'],
    green: ['#059669', '#10B981', '#34D399', '#6EE7B7', '#A7F3D0'],
    orange: ['#EA580C', '#F97316', '#FB923C', '#FDBA74', '#FED7AA'],
    blue: ['#2563EB', '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE'],
    yellow: ['#CA8A04', '#EAB308', '#FACC15', '#FDE047', '#FEF08A']
  },
  status: {
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444'
  }
};

// Card schemes from your original code
const CARD_SCHEMES = {
  purple: {
    background: 'bg-purple-50',
    icon: 'bg-purple-100 text-purple-600',
    trend: 'text-purple-600',
    text: 'text-purple-900',
    subtext: 'text-purple-600',
  },
  green: {
    background: 'bg-green-50',
    icon: 'bg-green-100 text-green-600',
    trend: 'text-green-600',
    text: 'text-green-900',
    subtext: 'text-green-600',
  },
  orange: {
    background: 'bg-orange-50',
    icon: 'bg-orange-100 text-orange-600',
    trend: 'text-orange-600',
    text: 'text-orange-900',
    subtext: 'text-orange-600',
  },
  blue: {
    background: 'bg-blue-50',
    icon: 'bg-blue-100 text-blue-600',
    trend: 'text-blue-600',
    text: 'text-blue-900',
    subtext: 'text-blue-600',
  }
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: ChartBar },
  { id: 'demographics', label: 'Demographics', icon: Users },

  { id: 'career', label: 'Career', icon: Briefcase },

  { id: 'batch', label: 'Batch Analysis', icon: Calendar }
];

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow border border-gray-100">
        <p className="font-medium text-gray-900">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm mt-1">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-500">{entry.name}:</span>
            <span className="font-medium text-gray-900">
              {typeof entry.value === 'number' && entry.name.toLowerCase().includes('rate')
                ? `${entry.value}%`
                : typeof entry.value === 'number' && entry.name.toLowerCase().includes('salary')
                ? `₹${(entry.value / 100000).toFixed(1)}L`
                : entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const AlumniAnalytics = () => {
  // State management
  const [analytics, setAnalytics] = useState(null);
  const [alumniData, setAlumniData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('365');
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Data fetching
  useEffect(() => {
    fetchData();
  }, [timeRange, selectedBatch, selectedDepartment]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [analyticsRes, alumniRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BASE_URL}/api/college/alumni-analytics?range=${timeRange}${
          selectedDepartment !== 'all' ? `&department=${selectedDepartment}` : ''
        }`, { headers }),
        fetch(`${process.env.REACT_APP_BASE_URL}/api/college/alumni`, { headers })
      ]);

      if (!analyticsRes.ok || !alumniRes.ok) 
        throw new Error('Failed to fetch data');

      const analyticsData = await analyticsRes.json();
      const alumniList = await alumniRes.json();

      setAnalytics(analyticsData);
      setAlumniData(alumniList);
      
      const uniqueDepts = [...new Set(alumniData.map(a => a.academicInfo?.department))].filter(Boolean);
      setDepartments(uniqueDepts);

    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Data filtering and processing
  const getFilteredAnalytics = () => {
    if (!analytics || !alumniData?.length) {
      return {
        total: 0,
        verifiedCount: 0,
        employedCount: 0,
        skillsDistribution: {},
        demographics: {
          total: 0,
          genderDistribution: {},
          locations: {}
        },
        academic: [],
        trends: {
          registration: [],
          employment: []
        },
        engagement: {
          events: { totalEvents: 0, avgParticipation: 0 },
          jobs: { totalJobs: 0, avgApplications: 0 }
        },
        departmentDistribution: {}
      };
    }

    const filteredAlumni = selectedBatch === 'all' 
    ? alumniData 
    : alumniData.filter(a => a.graduationYear === Number(selectedBatch));
    // Calculate metrics based on filtered alumni
    const verifiedCount = filteredAlumni.filter(a => a.isVerified).length;
    const employedCount = filteredAlumni.filter(a => a.professionalInfo?.currentlyEmployed).length;

    // Skills distribution
    const skillsDistribution = filteredAlumni.reduce((acc, curr) => {
      curr.professionalInfo?.skills?.forEach(skill => {
        if (skill) acc[skill] = (acc[skill] || 0) + 1;
      });
      return acc;
    }, {});

    // Department distribution
    const departmentDistribution = filteredAlumni.reduce((acc, curr) => {
      const dept = curr.academicInfo?.department || 'Unknown';
      if (!acc[dept]) {
        acc[dept] = { count: 0 };
      }
      acc[dept].count++;
      return acc;
    }, {});

    return {
      total: filteredAlumni.length,
      verifiedCount,
      employedCount,
      skillsDistribution,
      departmentDistribution,
      demographics: {
        total: filteredAlumni.length,
        genderDistribution: analytics.demographics?.genderDistribution || {},
        locations: analytics.demographics?.locations || {}
      },
      academic: filteredAlumni.reduce((acc, curr) => {
        const dept = curr.academicInfo?.department;
        if (!dept) return acc;
        
        if (!acc[dept]) {
          acc[dept] = {
            count: 0,
            achievements: []
          };
        }
        acc[dept].count++;
        if (curr.academicInfo?.achievements?.length) {
          acc[dept].achievements.push(...curr.academicInfo.achievements);
        }
        return acc;
      }, {}),
      trends: {
        registration: (analytics.trends?.registration || [])
          .filter(reg => selectedBatch === 'all' || 
            reg._id?.year === Number(selectedBatch)),
        employment: analytics.trends?.employment || []
      },
      engagement: {
        events: analytics.engagement?.events || { totalEvents: 0, avgParticipation: 0 },
        jobs: analytics.engagement?.jobs || { totalJobs: 0, avgApplications: 0 }
      },
      career: analytics.career || { avgCompaniesPerAlumni: 0, popularSkills: {} }
    };
  };

  // Shared components
  const MetricCard = ({ title, value, icon: Icon, trend, subtext, scheme = "purple" }) => {
    const colors = CARD_SCHEMES[scheme];
    return (
      <div className={`relative ${colors.background} rounded-2xl p-6 transition-all duration-300 
        hover:shadow-lg hover:scale-[1.02] overflow-hidden group`}
      >
        <div className="relative flex justify-between items-start">
          <div className={`p-3 rounded-xl ${colors.icon} transition-all duration-300 
            group-hover:scale-110`}>
            <Icon className="h-6 w-6" />
          </div>
          {trend !== undefined && (
            <div className={`flex items-center ${trend >= 0 ? 'text-green-600' : 'text-red-600'} 
              bg-white/80 px-2 py-1 rounded-lg`}>
              <span className="text-sm font-medium">
                {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
              </span>
              {trend >= 0 ? (
                <ChevronUp className="h-4 w-4 ml-1" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-1" />
              )}
            </div>
          )}
        </div>
        <div className="relative mt-4">
          <h3 className={`text-3xl font-bold ${colors.text}`}>
            {value}
          </h3>
          <p className="text-gray-600 text-sm mt-1 font-medium">{title}</p>
          {subtext && (
            <p className={`text-sm ${colors.subtext} mt-2`}>{subtext}</p>
          )}
        </div>
      </div>
    );
  };

  const ChartCard = ({ title, subtitle, children }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
  const prepareExportData = () => {
    const analytics = getFilteredAnalytics();
    
    return {
      overview: [
        ['Metric', 'Value'],
        ['Total Alumni', analytics.total],
        ['Verified Alumni', analytics.verifiedCount],
        ['Employed Alumni', analytics.employedCount],
        ['Employment Rate', `${((analytics.employedCount / analytics.total) * 100).toFixed(1)}%`],
        ['Total Skills', Object.keys(analytics.skillsDistribution || {}).length]
      ],
      
      demographics: [
        ['Category', 'Count', 'Percentage'],
        ...Object.entries(analytics.demographics.genderDistribution || {}).map(([gender, count]) => [
          gender.charAt(0).toUpperCase() + gender.slice(1),
          count,
          `${((count / analytics.total) * 100).toFixed(1)}%`
        ]),
      ],
      
      locations: [
        ['Location', 'Alumni Count'],
        ...Object.entries(analytics.demographics.locations || {})
          .filter(([loc]) => loc !== 'Unknown')
          .map(([location, count]) => [location, count])
      ],
      
      departments: [
        ['Department', 'Alumni Count', 'Percentage'],
        ...Object.entries(analytics.departmentDistribution || {}).map(([dept, data]) => [
          dept,
          data.count,
          `${((data.count / analytics.total) * 100).toFixed(1)}%`
        ])
      ],
      
      skills: [
        ['Skill', 'Count'],
        ...Object.entries(analytics.career?.popularSkills || {})
          .sort(([, a], [, b]) => b - a)
          .map(([skill, count]) => [skill, count])
      ]
    };
  };

  // Function to export to Excel
  const exportToExcel = () => {
    const data = prepareExportData();
    const wb = XLSX.utils.book_new();
    
    // Add each section as a separate worksheet
    Object.entries(data).forEach(([sheetName, sheetData]) => {
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(wb, ws, sheetName.charAt(0).toUpperCase() + sheetName.slice(1));
    });

    // Save the workbook
    XLSX.writeFile(wb, `alumni_analytics_${new Date().toLocaleDateString()}.xlsx`);
  };

  // Function to export to PDF
  const exportToPDF = () => {
    const data = prepareExportData();
    const doc = new jsPDF();
    
    // Set initial position
    let yPos = 20;
    
    // Add title
    doc.setFontSize(16);
    doc.text('Alumni Analytics Report', 20, yPos);
    yPos += 10;
    
    // Add date
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, yPos);
    yPos += 20;
    
    // Add each section
    Object.entries(data).forEach(([section, rows]) => {
      // Add section header
      doc.setFontSize(14);
      doc.setTextColor(82, 82, 255);
      doc.text(section.charAt(0).toUpperCase() + section.slice(1), 20, yPos);
      yPos += 10;
      
      // Reset text color
      doc.setTextColor(0);
      doc.setFontSize(10);
      
      // Add table data
      rows.forEach((row, index) => {
        // Check if we need a new page
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        
        // Format and add row
        const rowText = row.join(': ');
        doc.text(rowText, 30, yPos);
        yPos += 7;
      });
      
      yPos += 10;
    });
    
    // Save the PDF
    doc.save(`alumni_analytics_${new Date().toLocaleDateString()}.pdf`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 pb-32 pt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white">Alumni Analytics</h1>
              <p className="mt-2 text-purple-100">
                Comprehensive insights into your alumni network
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-4">
              {/* Time Range Filter */}
              <div className="relative">
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="appearance-none bg-white/10 text-white border border-purple-400/30 
                    rounded-lg pl-4 pr-10 py-2 focus:ring-2 focus:ring-white/20 focus:outline-none"
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last quarter</option>
                  <option value="365">Last year</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/70" />
              </div>
  
              {/* Batch Filter */}
              <div className="relative">
                <select
                  value={selectedBatch}
                  onChange={(e) => setSelectedBatch(e.target.value)}
                  className="appearance-none bg-white/10 text-white border border-purple-400/30 
                    rounded-lg pl-4 pr-10 py-2 focus:ring-2 focus:ring-white/20 focus:outline-none"
                >
                  <option value="all">All Batches</option>
                  {[...new Set(alumniData.map(a => a.graduationYear))]
                    .filter(Boolean)
                    .sort((a, b) => b - a)
                    .map(year => (
                      <option key={year} value={year}>Batch {year}</option>
                    ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/70" />
              </div>
  
              {/* Action Buttons */}
              <div className="flex gap-2">
                {/* Export Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 
                      hover:bg-white/20 transition-colors border border-purple-400/30 
                      text-white group"
                  >
                    <Download className="h-5 w-5 group-hover:scale-110 transition-transform" />
                    <span>Export</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>

                  {showExportMenu && (
                    <div className="absolute right-0 mt-2 w-48 rounded-lg bg-white shadow-lg 
                      ring-1 ring-black ring-opacity-5 z-50"
                      onBlur={() => setShowExportMenu(false)}
                    >
                      <div className="py-1">
                        <button
                          onClick={() => {
                            exportToExcel();
                            setShowExportMenu(false);
                          }}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 
                            hover:bg-gray-100 w-full text-left"
                        >
                          <FileSpreadsheet className="h-4 w-4 text-green-600" />
                          Export as Excel
                        </button>
                        <button
                          onClick={() => {
                            exportToPDF();
                            setShowExportMenu(false);
                          }}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 
                            hover:bg-gray-100 w-full text-left"
                        >
                          <FileText className="h-4 w-4 text-blue-600" />
                          Export as PDF
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                
                <button 
                  onClick={fetchData}
                  className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors
                    border border-purple-400/30 text-white group"
                >
                  <RefreshCw className="h-5 w-5 group-hover:scale-110 transition-transform" />
                </button>
              </div>
            </div>
         
           
          </div>
  
          {/* Tabs */}
          <div className="mt-12 border-b border-purple-400/30">
            <div className="flex gap-8 overflow-x-auto pb-px">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 pb-4 text-sm font-medium whitespace-nowrap
                    border-b-2 transition-all duration-300
                    ${activeTab === tab.id
                      ? 'text-white border-white'
                      : 'text-purple-100 border-transparent hover:text-white hover:border-white/50'
                    }
                  `}
                >
                  <tab.icon className={`h-4 w-4 ${activeTab === tab.id ? 'text-white' : 'text-purple-100'}`} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
  
      {/* Main Content Area */}
      <div className="-mt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-200 border-t-purple-600" />
                <p className="text-purple-600 font-medium">Loading dashboard data...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-white rounded-2xl p-8 text-center max-w-md mx-auto">
              <div className="bg-red-100 rounded-full p-3 w-12 h-12 mx-auto mb-4">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={fetchData}
                className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 
                  transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Overview Tab Content */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Key Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <MetricCard
                      title="Total Alumni"
                      value={getFilteredAnalytics().total.toLocaleString()}
                      icon={Users}
                      subtext="Registered members"
                      scheme="purple"
                    />
                    <MetricCard
                      title="Verified Alumni"
                      value={getFilteredAnalytics().verifiedCount.toLocaleString()}
                      icon={UserCheck}
                      subtext="Verified profiles"
                      scheme="green"
                    />
                    <MetricCard
                      title="Employed Alumni"
                      value={getFilteredAnalytics().employedCount.toLocaleString()}
                      icon={Briefcase}
                      subtext="Currently employed"
                      scheme="orange"
                    />
                    <MetricCard
                      title="Total Skills"
                      value={Object.keys(getFilteredAnalytics().skillsDistribution || {}).length.toLocaleString()}
                      icon={Award}
                      subtext="Unique skills"
                      scheme="blue"
                    />
                  </div>
  
                  {/* Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Registration Trends */}
                    <ChartCard 
                      title="Registration Trends" 
                      subtitle="Monthly registration and verification rates"
                    >
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart 
                            data={(getFilteredAnalytics()?.trends?.registration || [])
                              .map(reg => ({
                                month: `${reg._id.month}/${reg._id.year}`,
                                Total: reg.count,
                                Verified: reg.verifiedCount
                              }))}
                          >
                            <defs>
                              <linearGradient id="colorRegistrations" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={CHART_COLORS.primary.purple[0]} stopOpacity={0.3}/>
                                <stop offset="95%" stopColor={CHART_COLORS.primary.purple[0]} stopOpacity={0}/>
                              </linearGradient>
                              <linearGradient id="colorVerified" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={CHART_COLORS.primary.green[0]} stopOpacity={0.3}/>
                                <stop offset="95%" stopColor={CHART_COLORS.primary.green[0]} stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" stroke="#6b7280" />
                            <YAxis stroke="#6b7280" />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                              type="monotone"
                              dataKey="Total"
                              stroke={CHART_COLORS.primary.purple[0]}
                              fillOpacity={1}
                              fill="url(#colorRegistrations)"
                            />
                            <Area
                              type="monotone"
                              dataKey="Verified"
                              stroke={CHART_COLORS.primary.green[0]}
                              fillOpacity={1}
                              fill="url(#colorVerified)"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </ChartCard>
  
                    {/* Department Distribution */}
                   <ChartCard 
  title="Department Distribution" 
  subtitle="Alumni distribution across departments"
>
  <div className="h-80">
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={Object.entries(getFilteredAnalytics().departmentDistribution || {})
            .filter(([dept]) => dept !== 'Unknown')
            .map(([dept, data]) => ({
              name: dept,
              value: data.count
            }))}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={80}
          paddingAngle={5}
          dataKey="value"
        >
            
                              {Object.entries(getFilteredAnalytics().departmentDistribution || {})
                                .map((_, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={CHART_COLORS.primary[
                                      Object.keys(CHART_COLORS.primary)[index % Object.keys(CHART_COLORS.primary).length]
                                    ][0]}
                                  />
                                ))}
                            </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  </div>
</ChartCard>
                  </div>
  
                  {/* Location Stats */}
                  <ChartCard 
                    title="Geographic Distribution"
                    subtitle="Alumni presence across locations"
                  >
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={Object.entries(getFilteredAnalytics().demographics?.locations || {})
                            .filter(([loc]) => loc !== 'Unknown')
                            .map(([location, count]) => ({
                              location: location.split(',')[0],
                              count
                            }))}
                          layout="vertical"
                          margin={{ left: 100 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" />
                          <YAxis 
                            dataKey="location" 
                            type="category" 
                            width={90}
                            tick={{ fill: '#6b7280' }}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar 
                            dataKey="count" 
                            fill={CHART_COLORS.primary.blue[0]}
                            radius={[0, 4, 4, 0]}
                          >
                            {Object.entries(getFilteredAnalytics().demographics?.locations || {})
                              .filter(([loc]) => loc !== 'Unknown')
                              .map((_, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={CHART_COLORS.primary[
                                    Object.keys(CHART_COLORS.primary)[index % Object.keys(CHART_COLORS.primary).length]
                                  ][0]}
                                />
                              ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>
                </div>
              )}
  
              {/* Other tabs content... */}
{/* Demographics Tab */}
{activeTab === 'demographics' && (
  <div className="space-y-6">
    {/* Demographics Metrics */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <MetricCard
        title="Gender Ratio"
        value={`${((getFilteredAnalytics()?.demographics?.genderDistribution?.male || 0) / 
          (getFilteredAnalytics()?.total || 1) * 100).toFixed(1)}%`}
        icon={Users}
        subtext="Male to Female ratio"
        scheme="green"
      />
      <MetricCard
        title="Locations"
        value={Object.keys(getFilteredAnalytics()?.demographics?.locations || {})
          .filter(loc => loc !== 'Unknown').length}
        icon={Globe2}
        subtext="Represented cities"
        scheme="orange"
      />
    
    </div>

    {/* Gender and Location Distribution */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Gender Distribution */}
      <ChartCard 
        title="Gender Distribution" 
        subtitle="Alumni gender breakdown"
      >
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={Object.entries(getFilteredAnalytics()?.demographics?.genderDistribution || {})
                  .map(([gender, count]) => ({
                    name: gender.charAt(0).toUpperCase() + gender.slice(1),
                    value: count
                  }))}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {Object.entries(getFilteredAnalytics()?.demographics?.genderDistribution || {})
                  .map((_, index) => (
                    <Cell 
                      key={`cell-${index}`}
                      fill={CHART_COLORS.primary[
                        Object.keys(CHART_COLORS.primary)[index % 
                          Object.keys(CHART_COLORS.primary).length]
                      ][0]}
                    />
                  ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* Location Distribution */}
      <ChartCard 
        title="Geographic Distribution" 
        subtitle="Alumni by location"
      >
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={Object.entries(getFilteredAnalytics()?.demographics?.locations || {})
                .filter(([loc]) => loc !== 'Unknown')
                .map(([location, count]) => ({
                  location: location.split(',')[0],
                  count
                }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 10)}
              layout="vertical"
              margin={{ left: 100 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" />
              <YAxis 
                dataKey="location" 
                type="category" 
                width={90}
                tick={{ fill: '#6b7280' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="count" 
                fill={CHART_COLORS.primary.blue[0]}
                radius={[0, 4, 4, 0]}
              >
                {Object.entries(getFilteredAnalytics()?.demographics?.locations || {})
                  .filter(([loc]) => loc !== 'Unknown')
                  .map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={CHART_COLORS.primary.blue[index % 
                        CHART_COLORS.primary.blue.length]}
                    />
                  ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
    </div>
  </div>
)}

{/* Academic Tab */}

{activeTab === 'academic' && (
  <div className="space-y-6">
    {/* Academic Metrics */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <MetricCard
        title="Departments"
        value={Object.keys(getFilteredAnalytics()?.academic || {}).length}
        icon={Building2}
        subtext="Active departments"
        scheme="purple"
      />
      <MetricCard
        title="Achievement Rate"
        value={`${(Object.values(getFilteredAnalytics()?.academic || {}).reduce((sum, dept) => 
          sum + (dept.achievements?.length || 0), 0) / 
          (getFilteredAnalytics()?.total || 1) * 100).toFixed(1)}%`}
        icon={Award}
        subtext="Alumni with achievements"
        scheme="green"
      />
      <MetricCard
        title="Average Achievements"
        value={(Object.values(getFilteredAnalytics()?.academic || {}).reduce((sum, dept) => 
          sum + (dept.achievements?.length || 0), 0) / 
          (getFilteredAnalytics()?.total || 1)).toFixed(1)}
        icon={Star}
        subtext="Per alumni"
        scheme="orange"
      />
    </div>

    {/* Department Charts */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Department Distribution */}
      <ChartCard 
        title="Department Distribution" 
        subtitle="Alumni count by department"
      >
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={Object.entries(getFilteredAnalytics()?.academic || {})
                  .filter(([dept]) => dept && dept !== 'null' && dept !== 'undefined')
                  .map(([dept, data]) => ({
                    name: dept,
                    value: data.count
                  }))}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {Object.entries(getFilteredAnalytics()?.academic || {})
                  .filter(([dept]) => dept && dept !== 'null' && dept !== 'undefined')
                  .map((_, index) => (
                    <Cell 
                      key={`cell-${index}`}
                      fill={CHART_COLORS.primary[
                        Object.keys(CHART_COLORS.primary)[index % 
                          Object.keys(CHART_COLORS.primary).length]
                      ][0]}
                    />
                  ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* Achievement Distribution */}
      <ChartCard 
        title="Achievement Distribution" 
        subtitle="Achievements by department"
      >
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={Object.entries(getFilteredAnalytics()?.academic || {})
                .filter(([dept]) => dept && dept !== 'null' && dept !== 'undefined')
                .map(([dept, data]) => ({
                  department: dept,
                  achievements: data.achievements?.length || 0
                }))}
              layout="vertical"
              margin={{ left: 100 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis 
                dataKey="department" 
                type="category" 
                width={90}
                tick={{ fill: '#6b7280' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="achievements" 
                name="Achievements"
                fill={CHART_COLORS.primary.green[0]}
                radius={[0, 4, 4, 0]}
              >
                {Object.entries(getFilteredAnalytics()?.academic || {})
                  .filter(([dept]) => dept && dept !== 'null' && dept !== 'undefined')
                  .map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={CHART_COLORS.primary.green[index % 
                        CHART_COLORS.primary.green.length]}
                    />
                  ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
    </div>

    {/* Department Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Object.entries(getFilteredAnalytics()?.academic || {})
        .filter(([dept]) => dept && dept !== 'null' && dept !== 'undefined')
        .map(([dept, data], index) => (
          <div key={dept} 
            className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className={`p-3 rounded-lg ${
                CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].background
              }`}>
                <Building2 className={`h-6 w-6 ${
                  CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].trend
                }`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{dept}</h3>
                <p className="text-sm text-gray-500">{data.count} alumni</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Achievements</span>
                <span className="font-medium text-gray-900">
                  {data.achievements?.length || 0}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Department Share</span>
                <span className="font-medium text-gray-900">
                  {((data.count / getFilteredAnalytics().total) * 100).toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Achievement Rate</span>
                <span className="font-medium text-gray-900">
                  {((data.achievements?.length || 0) / data.count * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        ))}
    </div>
  </div>
)}
{/* Career Tab */}
{activeTab === 'career' && (
  <div className="space-y-6">
    {/* Career Metrics */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <MetricCard
        title="Employment Rate"
        value={`${((getFilteredAnalytics().employedCount / 
          (getFilteredAnalytics().total || 1)) * 100).toFixed(1)}%`}
        icon={Briefcase}
        subtext="Currently employed"
        scheme="purple"
      />
      <MetricCard
        title="Avg Companies"
        value={(getFilteredAnalytics()?.career?.avgCompaniesPerAlumni || 0).toFixed(1)}
        icon={Building}
        subtext="Per alumni"
        scheme="green"
      />
      <MetricCard
        title="Unique Skills"
        value={Object.keys(getFilteredAnalytics()?.career?.popularSkills || {}).length}
        icon={Award}
        subtext="Total skills"
        scheme="orange"
      />
    </div>

    {/* Career Charts */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Skills Distribution */}
      <ChartCard 
        title="Skills Distribution" 
        subtitle="Popular skills among alumni"
      >
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={Object.entries(getFilteredAnalytics()?.career?.popularSkills || {})
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 10)
                  .map(([skill, count]) => ({
                    name: skill,
                    value: count
                  }))}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {Object.entries(getFilteredAnalytics()?.career?.popularSkills || {})
                  .slice(0, 10)
                  .map((_, index) => (
                    <Cell 
                      key={`cell-${index}`}
                      fill={CHART_COLORS.primary[
                        Object.keys(CHART_COLORS.primary)[index % 
                          Object.keys(CHART_COLORS.primary).length]
                      ][0]}
                    />
                  ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* Employment Trends */}
      <ChartCard 
        title="Employment Trends" 
        subtitle="Employment statistics over time"
      >
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart 
              data={getFilteredAnalytics()?.trends?.employment || []}
              margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="_id.year"
                tick={{ fill: '#6b7280' }}
              />
              <YAxis tick={{ fill: '#6b7280' }} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="employmentCount"
                name="Employed Alumni"
                stroke={CHART_COLORS.primary.green[0]}
                strokeWidth={2}
                dot={{ fill: CHART_COLORS.primary.green[0] }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
    </div>

    {/* Top Companies and Skills */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Skills Breakdown */}
      <ChartCard 
        title="Top Skills" 
        subtitle="Most in-demand skills"
      >
        <div className="space-y-4">
          {Object.entries(getFilteredAnalytics()?.career?.popularSkills || {})
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5)
            .map(([skill, count], index) => {
              const maxCount = Math.max(...Object.values(getFilteredAnalytics()?.career?.popularSkills || {}));
              return (
                <div key={skill} className="flex flex-col gap-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-900">{skill}</span>
                    <span className="text-sm text-gray-500">{count} alumni</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${(count / maxCount) * 100}%`,
                        backgroundColor: CHART_COLORS.primary[
                          Object.keys(CHART_COLORS.primary)[index % Object.keys(CHART_COLORS.primary).length]
                        ][0]
                      }}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      </ChartCard>

      {/* Company Stats */}
    {/* Company Stats */}
<ChartCard 
  title="Top Employers" 
  subtitle="Companies with most alumni"
>
  <div className="space-y-4">
    {(() => {
      const companyStats = alumniData
        ?.filter(alumni => alumni.professionalInfo?.currentlyEmployed)
        .reduce((acc, alumni) => {
          const company = alumni.professionalInfo?.experiences?.[0]?.company;
          if (company) {
            acc[company] = (acc[company] || 0) + 1;
          }
          return acc;
        }, {});

      return Object.entries(companyStats || {})
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([company, count], index) => (
          <div 
            key={company}
            className="flex items-center justify-between p-4 bg-gray-50 rounded-xl
              hover:bg-gray-100 transition-all"
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].background
              }`}>
                <Building className={`h-5 w-5 ${
                  CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].trend
                }`} />
              </div>
              <div>
                <p className="font-medium text-gray-900">{company}</p>
                <p className="text-sm text-gray-500">{count} alumni</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">
                {((count / getFilteredAnalytics().employedCount) * 100).toFixed(1)}% of employed
              </p>
            </div>
          </div>
        ));
    })()}
  </div>
</ChartCard>
    </div>
  </div>
)}

{/* Engagement Tab */}
{activeTab === 'engagement' && (
  <div className="space-y-6">
    {/* Engagement Metrics */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <MetricCard
        title="Event Participation"
        value={getFilteredAnalytics()?.engagement?.events?.totalEvents || 0}
        icon={Users}
        trend={getFilteredAnalytics()?.engagement?.events?.avgParticipation || 0}
        subtext="Total participants"
        scheme="purple"
      />
      <MetricCard
        title="Active Jobs"
        value={getFilteredAnalytics()?.engagement?.jobs?.totalJobs || 0}
        icon={Briefcase}
        trend={getFilteredAnalytics()?.engagement?.jobs?.avgApplications ? 
          (getFilteredAnalytics().engagement.jobs.avgApplications * 100) : 0}
        subtext="Open positions"
        scheme="green"
      />
      <MetricCard
        title="Total Events"
        value={getFilteredAnalytics()?.engagement?.events?.totalEvents || 0}
        icon={Calendar}
        trend={getFilteredAnalytics()?.engagement?.events?.avgParticipation ? 
          (getFilteredAnalytics().engagement.events.avgParticipation * 100) : 0}
        subtext="Hosted events"
        scheme="orange"
      />
    </div>

    {/* Engagement Charts */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Event Participation Trends */}
      <ChartCard 
        title="Event Participation" 
        subtitle="Monthly participation rates"
      >
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart 
              data={getFilteredAnalytics()?.trends?.registration || []}
            >
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.primary.purple[0]} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={CHART_COLORS.primary.purple[0]} stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorVerified" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.primary.green[0]} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={CHART_COLORS.primary.green[0]} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="_id.month"
                tickFormatter={(value) => `Month ${value}`}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="count"
                name="Total Participants"
                stroke={CHART_COLORS.primary.purple[0]}
                fillOpacity={1}
                fill="url(#colorTotal)"
              />
              <Area
                type="monotone"
                dataKey="verifiedCount"
                name="Verified Participants"
                stroke={CHART_COLORS.primary.green[0]}
                fillOpacity={1}
                fill="url(#colorVerified)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* Department Engagement */}
      <ChartCard 
        title="Department Engagement" 
        subtitle="Participation by department"
      >
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart cx="50%" cy="50%" outerRadius="80%">
              <PolarGrid />
              <PolarAngleAxis 
                dataKey="department" 
              />
              <PolarRadiusAxis />
              <Radar
                name="Participation Rate"
                dataKey="rate"
                stroke={CHART_COLORS.primary.blue[0]}
                fill={CHART_COLORS.primary.blue[0]}
                fillOpacity={0.6}
                data={getFilteredAnalytics()?.academic
                  ?.filter(dept => dept._id !== null)
                  .map(dept => ({
                    department: dept._id,
                    rate: ((dept.count / getFilteredAnalytics().total) * 100).toFixed(1)
                  }))}
              />
              <Tooltip content={<CustomTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
    </div>

    {/* Recent Activities */}
    <ChartCard 
      title="Recent Activities" 
      subtitle="Latest alumni interactions"
    >
      <div className="space-y-4">
        {alumniData?.slice(0, 5).map((alumni, index) => (
          <div key={alumni._id} 
            className="flex items-center justify-between p-4 bg-gray-50 rounded-xl
              hover:bg-gray-100 transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center
                ${CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].background}`}>
                <Users className={`h-5 w-5 ${CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].trend}`} />
              </div>
              <div>
                <p className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  {alumni.firstName} {alumni.lastName}
                </p>
                <p className="text-sm text-gray-500">{alumni.department}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">
                {new Date(alumni.updatedAt).toLocaleDateString()}
              </p>
              <p className="text-sm text-gray-500">Profile Updated</p>
            </div>
          </div>
        ))}
      </div>
    </ChartCard>
  </div>
)}           
{/* Batch Analysis Tab */}
{activeTab === 'batch' && (
  <div className="space-y-6">
    {/* Batch Overview Metrics */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <MetricCard
        title="Total Batches"
        value={[...new Set(alumniData.map(a => a.graduationYear))].filter(Boolean).length}
        icon={Calendar}
        subtext="Graduation years"
        scheme="purple"
      />
      <MetricCard
        title="Largest Batch"
        value={Math.max(...Object.values(
          alumniData.reduce((acc, curr) => {
            if (curr.graduationYear) {
              acc[curr.graduationYear] = (acc[curr.graduationYear] || 0) + 1;
            }
            return acc;
          }, {}) || { 0: 0 }
        ))}
        icon={Users}
        subtext="Alumni in single batch"
        scheme="green"
      />
      <MetricCard
        title="Average Batch Size"
        value={Math.round(alumniData.length / 
          ([...new Set(alumniData.map(a => a.graduationYear))].filter(Boolean).length || 1)
        )}
        icon={ChartBar}
        subtext="Alumni per batch"
        scheme="orange"
      />
    </div>

    {/* Batch Performance Analysis */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Batch Size Distribution */}
      <ChartCard 
        title="Batch Size Distribution" 
        subtitle="Alumni count by graduation year"
      >
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={Object.entries(
                alumniData.reduce((acc, curr) => {
                  if (curr.graduationYear) {
                    acc[curr.graduationYear] = (acc[curr.graduationYear] || 0) + 1;
                  }
                  return acc;
                }, {})
              )
              .map(([year, count]) => ({
                year,
                count
              }))
              .sort((a, b) => Number(a.year) - Number(b.year))}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Alumni Count">
                {Object.entries(alumniData.reduce((acc, curr) => {
                  if (curr.graduationYear) {
                    acc[curr.graduationYear] = (acc[curr.graduationYear] || 0) + 1;
                  }
                  return acc;
                }, {})).map((_, index) => (
                  <Cell 
                    key={`cell-${index}`}
                    fill={CHART_COLORS.primary.purple[index % CHART_COLORS.primary.purple.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      
    </div>


  
  </div>
)}
 </>
          )}
        </div>
      </div>
    </div>

  );
};

  export default AlumniAnalytics;